import React, { useEffect, useState } from "react";
import { Server, CheckCircle2, XCircle, Loader2, Unplug, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VERIFY_LABELS = {
  incidents: "Incident Records", business_rules: "Business Rules", catalog_items: "Catalog Items",
};

export default function ServiceNowConnectPage() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ instance_url: "", username: "", password: "" });
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const loadStatus = () => api.get("/servicenow-connect/status").then((r) => setStatus(r.data));

  useEffect(() => { loadStatus(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      await api.post("/servicenow-connect/connect", form);
      toast.success("ServiceNow instance connected successfully!");
      setForm({ instance_url: "", username: "", password: "" });
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't connect to that instance");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.post("/servicenow-connect/disconnect");
    setStatus({ connected: false });
    setVerifyResult(null);
    toast.success("Disconnected from ServiceNow instance");
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/servicenow-connect/verify");
      setVerifyResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!status) return <div className="min-h-screen flex items-center justify-center" data-testid="servicenow-connect-loading"><div className="w-10 h-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="servicenow-connect-page">
      <div className="flex items-center gap-3 mb-2">
        <Server className="w-6 h-6 text-emerald-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Real Instance Verification · ServiceNow</span>
      </div>
      <h1 className="font-heading text-3xl font-semibold text-white">Connect Your ServiceNow PDI</h1>
      <p className="text-sm text-slate-400 mt-2">
        Connect any ServiceNow instance — even a free Personal Developer Instance (PDI) — and we'll verify, via read-only Table API calls,
        that you've actually created Incidents, Business Rules, and Catalog Items for real, the hands-on foundation this challenge is built on.
      </p>

      {status.connected ? (
        <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="servicenow-connected-panel">
          <div className="flex items-center gap-2 text-emerald-400 mb-3">
            <ShieldCheck className="w-5 h-5" /> <span className="font-semibold">Connected</span>
          </div>
          <p className="text-sm text-slate-400">Instance: <span className="text-white break-all">{status.instance_url}</span></p>
          <p className="text-sm text-slate-400 mt-1">Username: <span className="text-white">{status.username}</span></p>
          <p className="text-sm text-slate-400 mt-1">Connected: <span className="text-white">{new Date(status.connected_at).toLocaleString()}</span></p>

          <div className="flex items-center gap-3 mt-5">
            <button onClick={verify} disabled={verifying} data-testid="servicenow-verify-btn" className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Verify ServiceNow Setup
            </button>
            <button onClick={disconnect} data-testid="servicenow-disconnect-btn" className="flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300">
              <Unplug className="w-4 h-4" /> Disconnect
            </button>
          </div>

          {verifyResult && (
            <div className="mt-6 space-y-2.5" data-testid="servicenow-verify-results">
              {Object.entries(verifyResult).map(([key, r]) => (
                <div key={key} className="flex items-start gap-2.5" data-testid={`servicenow-verify-${key}`}>
                  {r.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-sm text-white">{VERIFY_LABELS[key] || key} {r.verified && `(${r.count} found)`}</p>
                    {r.error && <p className="text-xs text-slate-500 mt-0.5">{r.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="servicenow-setup-instructions">
            <h3 className="text-sm font-bold text-white mb-3">1. Get a free ServiceNow Personal Developer Instance</h3>
            <ol className="text-sm text-slate-400 space-y-1.5 list-decimal list-inside">
              <li>Go to developer.servicenow.com and sign up for a free account</li>
              <li>Request a Personal Developer Instance (PDI) — it spins up in a few minutes</li>
              <li>Note your instance URL (e.g. https://devXXXXX.service-now.com) and your admin username/password</li>
              <li>Create at least one Incident, one Business Rule, and one Catalog Item in your PDI for the best verification results</li>
            </ol>
          </div>

          <form onSubmit={connect} className="glass-panel rounded-2xl p-6 mt-6 space-y-3" data-testid="servicenow-connect-form">
            <h3 className="text-sm font-bold text-white mb-1">2. Enter your instance credentials</h3>
            <input value={form.instance_url} onChange={(e) => setForm((f) => ({ ...f, instance_url: e.target.value }))} placeholder="Instance URL (e.g. https://devXXXXX.service-now.com)" required data-testid="servicenow-instance-url-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Username" required data-testid="servicenow-username-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" required data-testid="servicenow-password-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <button type="submit" disabled={connecting} data-testid="servicenow-connect-submit-btn" className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Server className="w-4 h-4" /> Connect to ServiceNow</>}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
